import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, CalendarCheck, ClipboardList, FlaskConical, IndianRupee, Receipt, TrendingUp, UserPlus, Users, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import DataTable from "../components/common/DataTable";
import StatCard from "../components/common/StatCard";
import { erpApi, formatCurrency, formatDate } from "../services/erpApi";

export default function Dashboard() {
  const [summary, setSummary] = useState({});
  const [bills, setBills] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadDashboard = useCallback(async (showError = true) => {
    try {
      const [summaryRes, billsRes, patientsRes] = await Promise.all([erpApi.dashboard(), erpApi.bills(), erpApi.patients()]);
      setSummary(summaryRes.data);
      setBills(billsRes.data.slice(0, 5));
      setPatients(patientsRes.data.slice(0, 5));
    } catch (error) {
      if (showError) toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
    const interval = window.setInterval(() => loadDashboard(false), 30000);
    const refreshOnFocus = () => loadDashboard(false);
    window.addEventListener("focus", refreshOnFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshOnFocus);
    };
  }, [loadDashboard]);

  if (loading) {
    return <div className="h-64 animate-pulse rounded-lg bg-white" />;
  }

  const topServices = summary.top_services || [];
  const topDoctors = summary.top_doctors || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-manrope text-2xl font-bold">Daily Operations Dashboard</h1>
          <p className="text-sm text-hospital-text-muted">Reception, lab, billing, attendance and accounting snapshot.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/patients"><Button className="btn-primary"><UserPlus className="mr-2 h-4 w-4" />Patient</Button></Link>
          <Link to="/samples"><Button variant="outline"><FlaskConical className="mr-2 h-4 w-4" />Sample</Button></Link>
          <Link to="/billing"><Button variant="outline"><Receipt className="mr-2 h-4 w-4" />Bill</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Today's Patients" value={summary.patients_today || 0} icon={Users} to="/patients" />
        <StatCard title="Today's Revenue" value={formatCurrency(summary.total_revenue_today)} icon={IndianRupee} tone="success" to="/reports" />
        <StatCard title="Pending Reports" value={summary.pending_reports || 0} icon={ClipboardList} tone="warning" to="/samples" />
        <StatCard title="Pending Dues" value={formatCurrency(summary.pending_dues)} icon={Receipt} tone="warning" to="/billing" />
        <StatCard title="Attendance Today" value={summary.attendance_today || 0} icon={CalendarCheck} to="/attendance" />
        <StatCard title="Bills Today" value={summary.total_bills_today || 0} icon={Receipt} to="/billing" />
        <StatCard title="Monthly Expenses" value={formatCurrency(summary.monthly_expenses)} icon={Wallet} tone="warning" to="/expenses" />
        <StatCard title="Monthly Profit" value={formatCurrency(summary.monthly_profit_estimate)} icon={TrendingUp} tone="success" to="/analytics" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <Card className="border border-gray-200 xl:col-span-2">
          <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4 text-hospital-primary" />This Month</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-lg bg-emerald-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700">Income</p>
              <p className="mt-2 text-2xl font-bold text-emerald-800">{formatCurrency(summary.monthly_revenue)}</p>
            </div>
            <div className="rounded-lg bg-amber-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-amber-700">Expenses</p>
              <p className="mt-2 text-2xl font-bold text-amber-800">{formatCurrency(summary.monthly_expenses)}</p>
            </div>
            <div className="rounded-lg bg-hospital-primary-light p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-hospital-primary">Profit Estimate</p>
              <p className="mt-2 text-2xl font-bold text-hospital-primary">{formatCurrency(summary.monthly_profit_estimate)}</p>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader><CardTitle className="text-base">Attendance Summary</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {[
              ["Present", summary.attendance_present || 0],
              ["Late", summary.attendance_late || 0],
              ["Half Day", summary.attendance_half_day || 0],
              ["Absent Estimate", summary.attendance_absent_estimate || 0],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
                <span className="text-hospital-text-muted">{label}</span>
                <span className="font-semibold text-hospital-text-main">{value}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <DataTable
          title="Recent Bills"
          rows={bills}
          empty="No bills yet"
          columns={[
            { key: "patient_name", label: "Patient", render: (row) => <Link className="font-medium text-hospital-primary" to={`/patients/${row.patient_id}`}>{row.patient_name}</Link> },
            { key: "created_at", label: "Date", render: (row) => formatDate(row.created_at) },
            { key: "status", label: "Status", render: (row) => <span className="capitalize">{row.status}</span> },
            { key: "total", label: "Total", align: "right", render: (row) => formatCurrency(row.total) },
            { key: "action", label: "Action", render: (row) => <Link className="text-sm font-medium text-hospital-primary" to={`/patients/${row.patient_id}`}>View</Link> },
          ]}
        />
        <DataTable
          title="Recent Patients"
          rows={patients}
          empty="No patients registered"
          columns={[
            { key: "patient_uid", label: "ID", render: (row) => row.patient_uid || row.id?.slice(0, 8) },
            { key: "name", label: "Name", render: (row) => <Link className="font-medium text-hospital-primary" to={`/patients/${row.id}`}>{row.name}</Link> },
            { key: "mobile", label: "Mobile" },
            { key: "created_at", label: "Registered", render: (row) => formatDate(row.created_at) },
            { key: "action", label: "Action", render: (row) => <Link className="text-sm font-medium text-hospital-primary" to={`/billing/${row.id}`}>Bill</Link> },
          ]}
        />
      </div>

      <Card className="border border-gray-200">
        <CardHeader><CardTitle className="font-manrope text-base">Quick Actions</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Register Patient", "/patients"],
            ["Collect Sample", "/samples"],
            ["Create Invoice", "/billing"],
            ["Scan Attendance", "/attendance"],
          ].map(([label, path]) => (
            <Link key={label} to={path} className="rounded-lg border border-gray-200 bg-white p-4 text-sm font-semibold hover:border-hospital-primary hover:text-hospital-primary">
              {label}
            </Link>
          ))}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border border-gray-200">
          <CardHeader><CardTitle className="text-base">Top Services</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {topServices.length === 0 ? <p className="text-sm text-hospital-text-muted">No service data yet</p> : topServices.map((item) => (
              <div key={item.name} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                <div>
                  <p className="font-semibold text-hospital-text-main">{item.name}</p>
                  <p className="text-xs capitalize text-hospital-text-muted">{item.category || "service"} · {item.count} sold</p>
                </div>
                <p className="font-semibold text-hospital-primary">{formatCurrency(item.revenue)}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="border border-gray-200">
          <CardHeader><CardTitle className="text-base">Top Doctors / Referrals</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {topDoctors.length === 0 ? <p className="text-sm text-hospital-text-muted">No doctor billing data yet</p> : topDoctors.map((item) => (
              <div key={item.name} className="flex items-center justify-between rounded-lg border border-gray-100 p-3">
                <div>
                  <p className="font-semibold text-hospital-text-main">{item.name}</p>
                  <p className="text-xs text-hospital-text-muted">{item.count} bills</p>
                </div>
                <p className="font-semibold text-hospital-primary">{formatCurrency(item.revenue)}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
